import React from 'react';
import {
  Activity, BarChart3, Eye, FileText, Globe, Link2, TrendingUp,
} from 'lucide-react';
import { BarViz, DailyActivityChart, KpiCard, LoadingSkeleton } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrafficTab({ data }) {
  if (!data) return <LoadingSkeleton />;
  const categoryItems = (data.by_category || []).map(c => ({
    label: `${c.category} (${c.articles})`, value: `${c.views} views`, raw: c.views
  }));
  const maxViews = Math.max(...categoryItems.map(c => c.raw), 1);

  const dailyChartData = (data.daily_views || []).map(d => ({
    date: d.date, value: d.views, color: '#06b6d4',
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Eye} label="Total Views (All Time)" value={data.total_views?.toLocaleString() || 0} color="#10B981" />
        <KpiCard icon={Activity} label="Tracked Views" value={data.tracked_views || 0} subtext="real-time" color="#06B6D4" />
        <KpiCard icon={FileText} label="Articles Viewed" value={data.top_articles?.length || 0} color="#8B5CF6" />
        <KpiCard icon={BarChart3} label="Categories" value={data.by_category?.length || 0} color="#F59E0B" />
      </div>

      {/* Daily Views Chart */}
      <DailyActivityChart data={dailyChartData} label="Daily Article Views (Real-time)" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-teal-500" /> Top Articles by Views
          </h3>
          <div className="space-y-2">
            {data.top_articles?.slice(0, 15).map((a, i) => (
              <div key={a.slug} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-2 truncate mr-3">
                  <span className="text-gray-300 text-xs font-mono w-5">{i + 1}</span>
                  <span className="text-gray-700 truncate">{a.title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-gray-500">{a.category}</span>
                  <span className="text-gray-400 font-mono text-xs whitespace-nowrap">{a.view_count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Globe className="w-4 h-4 text-blue-500" /> Views by Category
            </h3>
            <BarViz items={categoryItems} maxVal={maxViews} color="#06b6d4" />
          </div>

          {data.tracked_top_articles?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <Activity className="w-4 h-4 text-cyan-500" /> Real-time Top Articles
              </h3>
              <div className="space-y-2">
                {data.tracked_top_articles.slice(0, 10).map((a, i) => (
                  <div key={a.slug} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-gray-700 truncate mr-3">{a.slug}</span>
                    <span className="text-gray-400 font-mono text-xs">{a.views} views</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.top_affiliate_links?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <Link2 className="w-4 h-4 text-orange-500" /> Top Affiliate Links Clicked
              </h3>
              <div className="space-y-2">
                {data.top_affiliate_links.slice(0, 10).map((l, i) => (
                  <div key={l.url} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-gray-700 truncate mr-3 text-xs">{l.url.length > 60 ? l.url.slice(0, 60) + '...' : l.url}</span>
                    <span className="text-gray-400 font-mono text-xs">{l.clicks} clicks</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
